<template>
  <div>
    <b-modal
      id="workplaceCreateModal"
      :title="title"
      size="lg"
      lazy
      :no-close-on-backdrop="true"
      ok-title="Vista"
      cancel-title="Hætta við"
      @ok.prevent="validateBeforeSubmit"
      :ok-disabled="submitting || loading"
      ref="createModal"
    >
      <div>
        <b-form @submit.prevent="validateBeforeSubmit" novalidate>
          <b-form-group
            label="Flokkun"
            label-for="types"
            :state="submitted && errors.has('types') ? false : null"
            :invalid-feedback="errors.first('types')"
            description="Veldu flokkun ef á við"
          >
            <v-select id="types" name="types" v-model="form.types" multiple :options="typeOptions" label="name"></v-select>
          </b-form-group>
          <b-form-group
            label="Heiti:"
            label-for="name"
            :state="submitted && errors.has('name') ? false : null"
            :invalid-feedback="errors.first('name')"
            description="Heiti starfsstaðar"
          >
            <b-form-input
              id="name"
              name="name"
              autofocus
              type="text"
              v-model="form.name"
              required
              :state="submitted && errors.has('name') ? false : null"
              v-validate="'required'"
              data-vv-as="heiti"
            ></b-form-input>
          </b-form-group>
          <b-form-group
            label="Hverfi:"
            label-for="district"
            :state="submitted && errors.has('district') ? false : null"
            :invalid-feedback="errors.first('district')"
            description="Veldu hverfi ef á við"
          >
            <b-form-select id="district" name="district" v-model="form.district">
              <option :value="null">-- Veldu hverfi --</option>
              <template v-if="!loading && workplaceDistricts.length > 0">
                <option v-for="district in workplaceDistricts" :key="district.id" :value="district.id">
                  {{ district.name }}
                </option>
              </template>
            </b-form-select>
          </b-form-group>
          <b-form-group label-for="enabled" description="Er starfsstaður virkur?">
            <b-form-checkbox id="enabled" name="enabled" autofocus v-model="form.enabled" data-vv-as="virkur"> Virkur </b-form-checkbox>
          </b-form-group>
        </b-form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { serverValidationToForm } from '@/utils';
import core from '@/api/core';

export default {
  name: 'workplace-create-modal',
  data() {
    return {
      submitting: false,
      submitted: false,
      title: 'Stofna starfsstað',
      id: null,
      errorList: [],
      typeOptions: [],
      workplaceDistricts: [],
      loading: false,
      form: {
        name: '',
        district: null,
        types: [],
        enabled: true,
      },
    };
  },
  async created() {
    await this.loadDropdowns();
  },
  methods: {
    async loadDropdowns() {
      this.loading = true;
      try {
        // Load workplace types
        const typesResponse = await core.dropdownWorkplacetypeList();
        this.typeOptions = typesResponse.data || [];

        // Load workplace districts
        const districtsResponse = await core.dropdownWorkplaceDistrictList();
        this.workplaceDistricts = districtsResponse.data || [];
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`);
        this.typeOptions = [];
        this.workplaceDistricts = [];
      } finally {
        this.loading = false;
      }
    },
    async loadWorkplace() {
      if (!this.id) return;

      this.loading = true;
      try {
        const response = await core.workplaceDetail(this.id);
        const workplace = response.data;

        if (workplace) {
          this.form.name = workplace.name || '';
          this.form.types = workplace.types
            ? workplace.types.filter(wt => wt.type).map(wt => ({ id: wt.type.id, name: wt.type.name }))
            : [];
          this.form.district = workplace.district ? workplace.district.id : null;
          this.form.enabled = workplace.enabled !== undefined ? workplace.enabled : true;
        }
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.loading = false;
      }
    },
    async validateBeforeSubmit() {
      try {
        this.submitted = true;
        this.errorList = [];
        await this.$validator.validateAll().then(response => {
          if (!response) throw 'FormValidationError'; // eslint-disable-line no-throw-literal
        });

        const data = {
          name: this.form.name,
          enabled: this.form.enabled,
          districtId: this.form.district,
          typeIds: this.form.types.map(t => t.id),
        };

        // Engar villur í formi.
        try {
          this.submitting = true;

          const response = this.id ? await core.workplaceUpdate(this.id, data) : await core.workplaceCreate(data);

          // Check for server validation errors
          if (response.data && response.data.errors) {
            serverValidationToForm({
              errors: response.data.errors,
              setFieldError: (field, error) => {
                this.errors.add({
                  field,
                  msg: error,
                  rule: 'server',
                });
              },
              errorFieldMap: { details: 'name' },
            });
          } else {
            if (this.id) {
              this.displaySuccess(`Starfsstaðnum „${this.form.name}“ hefur verið breytt.`);
            } else {
              this.displaySuccess(`Starfsstaðurinn „${this.form.name}“ hefur verið stofnaður.`);
            }
            this.submitted = false;
            this.$emit('successful', { id: this.id });
            this.$refs.createModal.hide();
            this.id = null;
          }
        } catch (e) {
          // Aðgerð mistókst.
          this.failed = true;
          this.$log.debug(e);
          this.displayError(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`);
        } finally {
          this.submitting = false;
        }
      } catch (e) {
        // Villur í formi.
        this.$log.debug(e);
      }
    },
    async show(id) {
      // Hreinsa formið
      this.submitted = false;
      this.errorList = [];
      this.form.name = '';
      this.form.types = [];
      this.form.district = null;
      this.form.enabled = true;
      this.id = null;
      this.title = 'Skrá starfsstað';

      if (id) {
        this.title = 'Breyta starfsstað';
        this.id = id;
        await this.loadWorkplace();
      }

      this.$refs.createModal.show();
    },
    ...mapActions({
      displayError: 'displayError',
      displaySuccess: 'displaySuccess',
    }),
  },
};
</script>
